import useTranslation from 'next-translate/useTranslation';

import {
  Accordion,
  Box,
  ImageApi,
  LinkTranslatedStyledAsLink,
  Paper,
  Typography,
} from '@core/component';
import { Translate } from '@core/constant';
import { useContextMediaQuery } from '@core/context';
import {
  EActionAttribute,
  EColor,
  EFontWeight,
  EIcon,
  ELinkAnimation,
  EPosition,
  ERouting,
  ETypographyVariant,
} from '@core/type';
import { FAQCategoriesEntity } from '@core/type/api';

import { CategoryBlockContentProps } from '../interface-faq';

export const CategoryBlock = ({ ...props }: FAQCategoriesEntity) => {
  const { isPhone } = useContextMediaQuery();

  return (
    <Paper
      padding={isPhone ? '20px' : '40px 30px'}
      boxShadow={EColor.ELEVATION_2}
      hoverStyles={{ borderShadow: EColor.ELEVATION_2 }}
    >
      <CategoryBlockContent {...props} />
    </Paper>
  );
};

export const CategoryBlockContent = ({
  name,
  subtitle,
  icon,
  faqArticles,
  withFaqLink,
  selectedArticleSlug,
}: FAQCategoriesEntity & CategoryBlockContentProps) => {
  const { t: tFaq } = useTranslation(Translate.page.FAQ);

  return (
    <Box display={'flex'} alignItems={'flex-start'}>
      {icon?.src?.baseUrl && (
        <Box
          position={'relative'}
          width={'30px'}
          height={'30px'}
          flexShrink={'0'}
          marginRight={'16px'}
        >
          <ImageApi {...icon} title={icon?.title} alt={icon?.alt} width={30} height={30} />
        </Box>
      )}
      <Accordion
        titleContent={
          <Box>
            <Typography as={EActionAttribute.H2} variant={ETypographyVariant.H5}>
              {name}
            </Typography>
            {subtitle && (
              <Typography color={EColor.INFO} marginTop={'8px'}>
                {subtitle}
              </Typography>
            )}
          </Box>
        }
        iconPosition={EPosition.TOP}
        width={'100%'}
      >
        <Box display={'flex'} flexDirection={'column'} rowGap={'16px'} paddingTop={'16px'}>
          {faqArticles?.map((article) => {
            const isSelectedArticle = article.slug === selectedArticleSlug;

            return (
              <Box key={article.name}>
                <LinkTranslatedStyledAsLink
                  keyRoute={ERouting.FAQ_SHOW}
                  query={{ slug: article.slug }}
                  color={isSelectedArticle ? EColor.ACCENT : EColor.PRIMARY}
                  fontWeight={isSelectedArticle ? EFontWeight.BOLD : EFontWeight.REGULAR}
                >
                  {article.name}
                </LinkTranslatedStyledAsLink>
              </Box>
            );
          })}
        </Box>
        {withFaqLink && (
          <Box textAlign={'right'} marginTop={'24px'} paddingRight={'16px'} paddingBottom={'16px'}>
            <LinkTranslatedStyledAsLink
              color={EColor.ACCENT}
              endIcon={EIcon.ARROW_DOWN}
              iconPosition={EPosition.RIGHT}
              iconColor={EColor.SECONDARY}
              animation={ELinkAnimation.ARROW_RIGHT}
              iconOrientation={EPosition.RIGHT}
              isIconStroke
              keyRoute={ERouting.FAQ}
            >
              {tFaq('seeAllTopics')}
            </LinkTranslatedStyledAsLink>
          </Box>
        )}
      </Accordion>
    </Box>
  );
};
